import { Lead } from '../types';

export const SELLDO_COLUMNS: readonly string[] = [
  'Created At', 'Salutation', 'First Name', 'Last Name', 'Lead Id',
  'Primary Phone', 'Secondary Phone', 'Primary Email', 'Secondary Email',
  'Minimum possesion', 'Maximum possesion', 'Minimum budget', 'Maximum budget',
  'Property types', 'Locations', 'BHK', 'Note', 'Followup date',
  'Site visit date', 'Age', 'Birthday', 'Anniversary', 'Industry',
  'Educational Details', 'Professional Details', 'Income', 'Loan Details',
  'Url', 'Address', 'State', 'City', 'Country', 'Project Ids', 'Sales Ids',
  'Campaign Ids', 'Sources', 'Sub-Sources / Sub-Campaigns',
  'Status in Given Stage', 'Lead Stages', 'Stage Change Reasons', 'Purpose',
  'Furnishing', 'Facing', 'Nri', 'Transaction Type', 'Funding Source',
  'Bathroom Preferences', 'Married', 'Manual Tags', 'Gender',
  'Channel Partner ID', 'Zip', 'Street', 'Area', 'Interested In',
];

const IST_TZ = 'Asia/Kolkata';

function pickPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find(p => p.type === type)?.value ?? '';
}

function formatDateTimeIST(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TZ,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  return `${pickPart(parts, 'day')}/${pickPart(parts, 'month')}/${pickPart(parts, 'year')} ${pickPart(parts, 'hour')}:${pickPart(parts, 'minute')}:${pickPart(parts, 'second')}`;
}

function formatDateIST(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TZ,
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).formatToParts(d);
  return `${pickPart(parts, 'day')}/${pickPart(parts, 'month')}/${pickPart(parts, 'year')}`;
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length === 12 && digits.startsWith('91')) return `+91 ${digits.slice(2)}`;
  if (digits.length === 10) return `+91 ${digits}`;
  return `+${digits}`;
}

function blankIfUnknown(value: string | null | undefined): string {
  if (!value) return '';
  return value.trim().toLowerCase() === 'unknown' ? '' : value;
}

export function leadToSellDoRow(lead: Lead): Record<string, string> {
  const row: Record<string, string> = {};
  for (const col of SELLDO_COLUMNS) row[col] = '';

  row['Created At'] = formatDateTimeIST(lead.created_at);
  row['First Name'] = lead.name ?? '';
  row['Lead Id'] = lead.id ?? '';
  row['Primary Phone'] = formatPhone(lead.mobile);
  row['Secondary Phone'] = formatPhone(lead.alternate_mobile);
  row['Primary Email'] = lead.email ?? '';
  row['Minimum budget'] = lead.budget_range_min != null ? String(lead.budget_range_min) : '';
  row['Maximum budget'] = lead.budget_range_max != null ? String(lead.budget_range_max) : '';
  row['Property types'] = blankIfUnknown(lead.preferred_villa_type);
  row['Locations'] = blankIfUnknown(lead.location);
  row['Followup date'] = formatDateIST(lead.next_followup_date);
  row['Sources'] = lead.source ?? '';
  // Sell.do's own label for the lead's stage there (the import format is Sell.do's).
  row['Lead Stages'] = blankIfUnknown(lead.crm_stage_label ?? null);
  row['Purpose'] = blankIfUnknown(lead.primary_motivation);
  row['Transaction Type'] = blankIfUnknown(lead.investor_type);
  row['Funding Source'] = blankIfUnknown(lead.needs_bank_loan);
  // 'Manual Tags' is a fixed Sell.do import column — only operator-authored
  // manual: tags belong there, never the call-derived dimension:value tags.
  row['Manual Tags'] = (lead.tags ?? [])
    .filter(t => t.startsWith('manual:'))
    .map(t => t.slice('manual:'.length))
    .join(', ');

  return row;
}

function escapeCsvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function buildCsv(leads: Lead[]): string {
  const lines: string[] = [SELLDO_COLUMNS.map(escapeCsvCell).join(',')];
  for (const lead of leads) {
    const row = leadToSellDoRow(lead);
    lines.push(SELLDO_COLUMNS.map(c => escapeCsvCell(row[c])).join(','));
  }
  return lines.join('\r\n');
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function generateExportFilename(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TZ,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  return `leads-export-${pickPart(parts, 'year')}${pickPart(parts, 'month')}${pickPart(parts, 'day')}-${pickPart(parts, 'hour')}${pickPart(parts, 'minute')}.csv`;
}
